import React, { useEffect } from 'react';
import _ from 'lodash';
import { IconButton } from '@material-ui/core';
import PropTypes from 'prop-types';
import DefaultTextField from 'components/Utils/DefaultTextField';
import Icon from 'components/Utils/NewIcon';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import DocumentTemplateUtils from 'services/DocumentTemplateUtils';
import { SmartInputOptions } from 'components/HealthRecords/Documents/DocumentTemplate/SmartInput';
import { DefaultCheckbox, DefaultSwitch as Switch } from 'services/UIUtils';
import DocumentTemplateFields from 'components/HealthRecords/Documents/DocumentTemplate/DocumentTemplateInputs';
import { EDITABLE_BY_ROLES } from './EditableByRolesEnum';
import PlaceholderField from 'components/HealthRecords/Documents/DocumentTemplate/Utils/PlaceholderField';
import DefaultButton from '../../../../Utils/NewDefaultButton';
import PerformanceUtils from '../../../../../services/PerformanceUtils';

const SelectedElementEditPanel = (props) => {
  const { selectedEditElement, updateField, copyStyle, pasteStyle, removeField, getEditElement } =
    props;
  if (!selectedEditElement) return null;

  let editElement = getEditElement(selectedEditElement);

  if (!editElement) return null;

  let id = editElement.id || '';
  let type = editElement.type || '';
  let style = editElement.style || '';
  let description = editElement.description || '';
  let editableBy = editElement.editable_by || EDITABLE_BY_ROLES.everyone;

  return (
    <div className="row edit-element">
      <span className="small-title">Edit Element</span>
      <div className="icons">
        <Icon
          src="copy"
          alt="Copy Style"
          showTooltip={true}
          className="mr-2"
          height="20px"
          onClick={() => copyStyle(editElement)}
        />
        <Icon
          src="paste"
          alt="Paste Style"
          className="mr-2"
          showTooltip={true}
          height="20px"
          onClick={() => pasteStyle(editElement)}
        />
        <Icon
          showTooltip={true}
          src="trash"
          height="20px"
          className="mr-2"
          onClick={() => removeField(id)}
        />
        <Icon
          showTooltip={true}
          src="xmark"
          height="20px"
          className="mr-2"
          onClick={() => props.selectEditElement(null)}
        />
      </div>
      <DefaultTextField label="Id" name="id" value={id} disabled={true} />
      <DefaultTextField
        label="Description"
        name="description"
        value={description}
        onChange={(event) => updateField(id, { description: event.target.value })}
        disabled={!editElement}
      />
      <DefaultSelectField
        label="Type"
        width="250px"
        disabled={!editElement}
        value={type}
        onChange={(event) => updateField(id, { type: event.target.value })}
        inputs={DocumentTemplateUtils.getInputTypes()}
      />
      <DefaultSelectField
        label="Editable by"
        width="250px"
        disabled={!editElement}
        onChange={(event) => updateField(id, { editable_by: event.target.value })}
        value={editableBy}
        inputs={Object.values(EDITABLE_BY_ROLES)}
      />
      {type === 'checkbox' && <CheckboxOptions element={editElement} updateField={updateField} />}
      {type === 'textarea' && <TextAreaOptions element={editElement} updateField={updateField} />}
      {type === 'textfield' && <TextFieldOptions element={editElement} updateField={updateField} />}
      {type === 'smart' && (
        <SmartInputOptions key={id} {...props} id={id} type={type} editElement={editElement} />
      )}
      {(type === 'stamp' || type === 'patient stamp') && (
        <StampFieldOptions element={editElement} updateField={updateField} />
      )}
      {type === 'signature' && (
        <SignatureOptions
          element={editElement}
          updateField={updateField}
          isPatientSignature={false}
        />
      )}
      {type === 'patient signature' && (
        <SignatureOptions element={editElement} updateField={updateField} isPatientSignature />
      )}
      <DefaultTextField
        label="Style"
        name="style"
        value={style}
        onChange={(event) => updateField(id, { style: event.target.value })}
        disabled={!editElement}
      />
    </div>
  );
};

const SignatureOptions = ({ element, updateField, isPatientSignature }) => {
  const { id, scale } = element;

  useEffect(() => {
    updateField(id, { isPatient: isPatientSignature });
  }, [isPatientSignature]);

  return (
    <>
      <div className="slider" style={{ marginTop: '10px' }}>
        <span style={{ marginRight: '10px' }}>Size</span>
        <input
          type="range"
          min="0.1"
          max="2.0"
          value={scale || 1}
          step="0.1"
          onChange={(event) => {
            let value = event.target.value;
            updateField(id, { scale: value });
          }}
        />
      </div>
    </>
  );
};

const CheckboxOptions = ({ element, updateField }) => {
  const { id, scale, metadata, circle, circle_scale } = element;
  return (
    <>
      <PlaceholderField element={element} updateField={updateField} />
      <SliderInput
        min={0.1}
        max={5.0}
        step={0.1}
        value={scale || 1}
        onValue={(value) => updateField(id, { scale: value })}
        title="Scale"
      />
      <Switch
        label="Circle"
        checked={circle}
        onChange={(event, isInputChecked) => updateField(id, { circle: isInputChecked })}
      />
      {circle && (
        <SliderInput
          min={0.1}
          max={1.0}
          step={0.1}
          value={circle_scale || 1}
          onValue={(value) => updateField(id, { circle_scale: value })}
          title="Circle Shape"
        />
      )}
      <DefaultTextField
        label="Metadata"
        name="metadata"
        value={metadata}
        onChange={(event) => updateField(id, { metadata: event.target.value })}
        disabled={!element}
      />
    </>
  );
};

const TextAreaOptions = ({ element, updateField }) => {
  const { id, letter_spacing, line_height, text_indent, font_size = 14 } = element;
  return (
    <>
      <PlaceholderField element={element} updateField={updateField} />
      <SliderInput
        min={0}
        max={20}
        step={0.1}
        value={letter_spacing}
        onValue={(value) => updateField(id, { letter_spacing: value })}
        title="Letter Spacing"
      />
      <SliderInput
        min={15}
        max={50}
        step={0.1}
        value={line_height}
        onValue={(value) => updateField(id, { line_height: value })}
        title="Line Height"
      />
      <SliderInput
        min={0}
        max={300}
        step={1}
        value={text_indent}
        onValue={(value) => updateField(id, { text_indent: value })}
        title="Text Indent"
      />
      <SliderInput
        min={5}
        max={60}
        step={1}
        value={font_size}
        onValue={(value) => updateField(id, { font_size: value })}
        title="Font Size"
      />
    </>
  );
};

const StampFieldOptions = ({ element, updateField }) => {
  const { id, rotation, scale, opacity } = element;
  return (
    <>
      <PlaceholderField element={element} updateField={updateField} />
      <SliderInput
        min={0.1}
        max={2}
        step={0.1}
        value={scale}
        onValue={(value) => updateField(id, { scale: value || 0 })}
        title="Scale"
        defaultValue={1.0}
      />
      <SliderInput
        min={0}
        max={360}
        step={1}
        value={rotation}
        onValue={(value) => updateField(id, { rotation: value || 0 })}
        title="Rotation"
        defaultValue={350}
      />
      <SliderInput
        min={0}
        max={1}
        step={0.05}
        value={opacity}
        onValue={(value) => updateField(id, { opacity: value || 0 })}
        title="Opacity"
        defaultValue={0.8}
      />
    </>
  );
};

const TextFieldOptions = ({ element, updateField }) => {
  const { id, letter_spacing, background, font_size = 14, metadata } = element;
  return (
    <>
      <PlaceholderField element={element} updateField={updateField} />
      <SliderInput
        min={0}
        max={20}
        step={0.1}
        value={letter_spacing}
        onValue={(value) => updateField(id, { letter_spacing: value })}
        title="Letter Spacing"
      />
      <SliderInput
        min={5}
        max={60}
        step={1}
        value={font_size}
        onValue={(value) => updateField(id, { font_size: value })}
        title="Font Size"
      />
      <Checkbox
        title="White Background"
        value={background === 'white'}
        onChange={(value) => updateField(id, { background: value ? 'white' : 'none' })}
      />
      <DefaultTextField
        key={id}
        label="Metadata"
        name="metadata"
        value={metadata}
        onChange={(event) => updateField(id, { metadata: event.target.value })}
        disabled={!element}
      />
    </>
  );
};

const Checkbox = ({ title, value, onChange }) => (
  <div style={{ marginTop: '10px' }}>
    <DefaultCheckbox
      name={title}
      value={value}
      onChange={() => onChange(!value)}
      style={{ display: 'inline-block', margin: 0 }}
      text={title}
    />
  </div>
);

const SliderInput = ({
  min = 0,
  max = 300,
  step = 1,
  onValue,
  title,
  defaultValue = min,
  value = defaultValue,
}) => {
  return (
    <div className="slider" style={{ marginTop: '10px' }}>
      {!!title && <span style={{ marginRight: '10px' }}>{title}</span>}
      <input
        type="range"
        min={'' + min}
        max={'' + max}
        value={'' + (value || defaultValue)}
        step={'' + step}
        onChange={(event) => {
          let value = event.target.value;
          if (value === '0') value = '';
          onValue(value);
        }}
      />
    </div>
  );
};

export const FloatingField = ({
  coords,
  pageNumber,
  prepareNewElement,
  pageWidth,
  pageHeight,
  prefill,
}) => {
  if (!coords) return null;

  let { x, y } = coords;
  let newElement = prepareNewElement(pageNumber, x, y);

  return coords ? (
    <div
      style={{
        pointerEvents: 'none',
        display: 'contents',
      }}
      className="floating-field"
    >
      <DocumentTemplateFields
        editMode={true}
        fieldValues={{}}
        inputs={[newElement]}
        pageWidth={pageWidth}
        pageHeight={pageHeight}
        index={pageNumber}
        onChange={() => {}}
        prefill={prefill}
      />
    </div>
  ) : null;
};

SelectedElementEditPanel.propTypes = {
  selectedEditElement: PropTypes.string,
  selectEditElement: PropTypes.func,
  updateField: PropTypes.func,
  copyStyle: PropTypes.func,
  pasteStyle: PropTypes.func,
  removeField: PropTypes.func,
  getEditElement: PropTypes.func,
};

export const MultipleSelectedElementsPanel = ({
  selectedElements,
  getEditElement,
  onChange,
  templateEdit,
}) => {
  function align(alignment) {
    const myEdit = PerformanceUtils.clone(templateEdit);
    const elements = myEdit.fields
      .flatMap((page) => page.inputs)
      .filter((it) => selectedElements.has(it.id));
    let performUpdate = true;
    switch (alignment) {
      case 'left': {
        const minX = `${_(elements).map('x_percent').map(parseFloat).min()}%`;
        elements.forEach((it) => {
          it.x_percent = minX;
        });
        break;
      }
      case 'right': {
        const maxX = _(elements)
          .map((it) => parseFloat(it.x_percent) + parseFloat(it.w_percent))
          .max();
        elements.forEach((it) => {
          it.x_percent = `${maxX - parseFloat(it.w_percent)}%`;
        });
        break;
      }
      case 'top': {
        const minY = `${_(elements).map('y_percent').map(parseFloat).min()}%`;
        elements.forEach((it) => {
          it.y_percent = minY;
        });
        break;
      }
      case 'bottom': {
        const maxY = _(elements)
          .map((it) => parseFloat(it.y_percent) + parseFloat(it.h_percent))
          .max();
        elements.forEach((it) => {
          it.y_percent = `${maxY - parseFloat(it.h_percent)}%`;
        });
        break;
      }
      case 'center-v':
      case 'center-h': {
        const points = elements.flatMap((it) => {
          return [
            { x: parseFloat(it.x_percent), y: parseFloat(it.y_percent) },
            {
              x: parseFloat(it.x_percent) + parseFloat(it.w_percent),
              y: parseFloat(it.y_percent) + parseFloat(it.h_percent),
            },
          ];
        });
        const minX = _(points).map('x').min();
        const maxX = _(points).map('x').max();
        const minY = _(points).map('y').min();
        const maxY = _(points).map('y').max();

        const midPoint = {
          x: minX + (maxX - minX) / 2,
          y: minY + (maxY - minY) / 2,
        };

        elements.forEach((it) => {
          const newX = midPoint.x - parseFloat(it.w_percent) / 2;
          const newY = midPoint.y - parseFloat(it.h_percent) / 2;
          if (alignment === 'center-v') {
            it.y_percent = `${newY}%`;
          } else {
            it.x_percent = `${newX}%`;
          }
        });

        break;
      }
      case 'distribute-v':
      case 'distribute-h': {
        const points = elements.flatMap((it) => {
          return [
            {
              x: parseFloat(it.x_percent),
              y: parseFloat(it.y_percent),
              id: it.id,
              w_percent: parseFloat(it.w_percent),
              h_percent: parseFloat(it.h_percent),
            },
            {
              x: parseFloat(it.x_percent) + parseFloat(it.w_percent),
              y: parseFloat(it.y_percent) + parseFloat(it.h_percent),
              id: it.id,
              w_percent: parseFloat(it.w_percent),
              h_percent: parseFloat(it.h_percent),
            },
          ];
        });
        const minX = _(points).minBy('x');
        const maxX = _(points).maxBy('x');
        const minY = _(points).minBy('y');
        const maxY = _(points).maxBy('y');
        const axisCoord = alignment === 'distribute-v' ? 'y_percent' : 'x_percent';
        const axisSize = alignment === 'distribute-v' ? 'h_percent' : 'w_percent';

        let elementsToArrange =
          alignment === 'distribute-v'
            ? elements.filter((it) => it.id !== minY.id && it.id !== maxY.id)
            : elements.filter((it) => it.id !== minX.id && it.id !== maxX.id);

        elementsToArrange = _.sortBy(elementsToArrange, (it) => parseFloat(it[axisCoord]));

        const minBound =
          alignment === 'distribute-v' ? minY.y + minY.h_percent : minX.x + minX.w_percent;

        const maxBound =
          alignment === 'distribute-v' ? maxY.y - maxY.h_percent : maxX.x - maxX.w_percent;

        const coveredArea = _(elementsToArrange).map(axisSize).map(parseFloat).sum();
        const totalArea = maxBound - minBound;

        const gap = (totalArea - coveredArea) / (elementsToArrange.length + 1);

        let base = minBound + gap;
        for (let element of elementsToArrange) {
          element[axisCoord] = `${base}%`;
          base += parseFloat(element[axisSize]) + gap;
        }

        break;
      }
      default: {
        performUpdate = false;
      }
    }
    if (performUpdate) {
      onChange('templateEdit', myEdit);
    }
  }

  return (
    <div className="row edit-element">
      <span className="small-title">Selected Elements</span>
      <div className="section-alignment flex gap-4 justify-center">
        <div className="flex gap-1">
          <IconButton size="small" onClick={() => align('left')}>
            <Icon src="objects-align-left" />
          </IconButton>
          <IconButton size="small" onClick={() => align('center-h')}>
            <Icon src="objects-align-center-horizontal" />
          </IconButton>
          <IconButton size="small" onClick={() => align('right')}>
            <Icon src="objects-align-right" />
          </IconButton>
        </div>
        <div className="flex gap-1">
          <IconButton size="small" onClick={() => align('top')}>
            <Icon src="objects-align-top" />
          </IconButton>
          <IconButton size="small" onClick={() => align('center-v')}>
            <Icon src="objects-align-center-vertical" />
          </IconButton>
          <IconButton size="small" onClick={() => align('bottom')}>
            <Icon src="objects-align-bottom" />
          </IconButton>
        </div>
        <div className="flex gap-1">
          <IconButton size="small" onClick={() => align('distribute-h')}>
            <Icon src="distribute-spacing-horizontal" />
          </IconButton>
          <IconButton size="small" onClick={() => align('distribute-v')}>
            <Icon src="distribute-spacing-vertical" />
          </IconButton>
        </div>
      </div>
    </div>
  );
};

export default SelectedElementEditPanel;
